import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useViewCart } from "@/api/hooks";
import { ApiError, isCapRequired } from "@/api/client";
import { ErrorMessage } from "@/components/ErrorMessage";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ExpressCartView } from "@/components/express-cart/ExpressCartView";
import { CaptchaGate } from "@/components/express-cart/CaptchaGate";
import type { CartSession } from "@/hooks/useCartSession";
import { readSessionCartToken, saveSessionCartToken, takeExpressCartNotice } from "@/lib/express-cart/device";
import { getSessionId, getSessionToken, setSessionId, setSessionToken } from "@/lib/utils";

/**
 * Reads the agent's View Cart link, /cart?session={sessionId}&token={token},
 * keeps the token for refreshes, and removes both from the address bar.
 */
function useViewCartSession(): CartSession | null {
  const [session] = useState<CartSession | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session");
    if (!sessionId) return null;
    const token = params.get("token");
    if (token) {
      saveSessionCartToken(sessionId, token);
      // The cart link's token is authoritative for this browser: keep the
      // global session token/id in sync so authenticated calls made on the
      // cart page (analytics included) send the right token.
      if (!getSessionToken() || !getSessionId() || getSessionId() !== sessionId) {
        setSessionToken(token);
        setSessionId(sessionId);
      }
    }
    const sessionToken = token ?? readSessionCartToken(sessionId);
    return sessionToken ? { session_id: sessionId, session_token: sessionToken } : null;
  });

  useEffect(() => {
    if (!session) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    // Keep the session ID so a refresh can find the stored token.
    url.searchParams.set("session", session.session_id);
    window.history.replaceState({}, "", url.toString());
  }, [session]);

  return session;
}

/**
 * Express cart for an existing session: where the agent's View Cart link sends
 * shoppers to choose variants and quantities before checkout. Rendered outside
 * SessionGate so the stored checkout session is only replaced on Continue.
 */
export function SessionCartPage() {
  const session = useViewCartSession();
  const { data, isLoading, error, refetch } = useViewCart(session);
  // Set by the checkout when it sends the shopper back, e.g. stock ran out; shown once.
  const [notice] = useState(() => (session ? takeExpressCartNotice(session.session_id) : null));

  if (!session) {
    // The old /cart route: back to the stored checkout session, if any.
    const storedSessionId = getSessionId();
    if (storedSessionId && !new URLSearchParams(window.location.search).has("session")) {
      return <Navigate to={`/order/${storedSessionId}`} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary) p-4">
        <ErrorMessage
          title="This cart link is incomplete"
          message="Open your cart again from your WhatsApp chat."
          className="w-full max-w-md"
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary)">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // A refused load (429 requiresCap) retries through the CAP gate instead.
  if (error && isCapRequired(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary) p-4">
        <CaptchaGate error={error} onRetry={refetch} />
      </div>
    );
  }

  if (error || !data) {
    const expired = error instanceof ApiError && error.statusCode === 401;
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-bg-secondary) p-4">
        <ErrorMessage
          title={expired ? "This cart link has expired" : "Something went wrong"}
          message={
            expired
              ? "Ask for your cart again in your WhatsApp chat to get a new link."
              : "We couldn't load your cart. Please try again."
          }
          detail={import.meta.env.DEV && error instanceof Error ? error.message : undefined}
          className="w-full max-w-md"
        />
      </div>
    );
  }

  return <ExpressCartView data={data} source="view-cart" notice={notice} />;
}
